// components/Register.js
import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Visibility as VisibilityIcon, VisibilityOff as VisibilityOffIcon } from "@mui/icons-material";
import LocationAutocomplete from "./CustomMui/LocationStreetAutocomplete";

function Register() {
  const navigate = useNavigate();

  // Compute today's date in YYYY-MM-DD format and current year
  const today = new Date();
  const yyyy = today.getFullYear();
  const mm = String(today.getMonth() + 1).padStart(2, "0");
  const dd = String(today.getDate()).padStart(2, "0");
  const todayString = `${yyyy}-${mm}-${dd}`;
  const currentYear = yyyy;
  const currentMonth = today.getMonth()+1; //Current month with a one-based index

  // Basic login fields
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  // Additional company registration fields
  const [companyName, setCompanyName] = useState("");
  const [companyAddress, setCompanyAddress] = useState("");
  const [billingAddress, setBillingAddress] = useState("");
  const [description, setDescription] = useState("");
  const [membershipLevel, setMembershipLevel] = useState("");
  const [primaryContact, setPrimaryContact] = useState("");
  const [primaryContactEmail, setPrimaryContactEmail] = useState("");
  // Optional fields for alumni check
  const [isAlumni, setIsAlumni] = useState("No");
  const [primaryContactMajor, setPrimaryContactMajor] = useState("");

  // Partnership date fields
  const [membershipStartDate, setMembershipStartDate] = useState(todayString);
  const [membershipEndDate, setMembershipEndDate] = useState(todayString);

  // For potential errors and success messages
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  // For toggling password visibility
  const [showPassword, setShowPassword] = useState(false);
  const handleClickShowPassword = () => setShowPassword((prev) => !prev);

  // NEW: State to track if Billing Address is same as Company Address
  const [billingSame, setBillingSame] = useState(false);

  // Update Billing Address if billingSame is checked
  useEffect(() => {
    if (billingSame) {
      setBillingAddress(companyAddress);
    }
  }, [billingSame, companyAddress]);

  // Use a ref to track the previous membership level
  const prevMembershipLevelRef = useRef(membershipLevel);

  // When switching from "associate" or "careerfaironly" to any other partnership, reset start date to today.
  // Also reset for TALENT/ENGAGEMENT/STRATEGIC tiers to July 1st.
  useEffect(() => {
    if (
      prevMembershipLevelRef.current === "careerfaironly" && membershipLevel !== "careerfaironly"
    ) {
      setMembershipStartDate(todayString);
    }
    if (membershipLevel === "talent" || membershipLevel === "engagement" || membershipLevel === "strategic") {
      const startDate = currentMonth >= 7 ? `${currentYear}-07-01` : `${currentYear}-07-01`;
      const endYear = currentMonth >= 7 ? currentYear + 1 : currentYear + 1;
      setMembershipStartDate(startDate);
      setMembershipEndDate(`${endYear}-06-30`);
    }
    prevMembershipLevelRef.current = membershipLevel;
  }, [membershipLevel, todayString, currentYear, currentMonth]);

  // Compute membership end date whenever membershipStartDate or membershipLevel changes.
  useEffect(() => {
    if (membershipLevel === "careerfaironly") {
      // For career fair only, force start date to one of the allowed values.
      if(currentMonth >= 7) { //If current month at or past 7, then spring needs to use next year
        if (
          membershipStartDate !== `${currentYear+1}-02-01` &&
          membershipStartDate !== `${currentYear}-09-01`
        ) {
          setMembershipStartDate(`${currentYear}-09-01`);
          return;
        }
        // Set end date based on start date
        if (membershipStartDate === `${currentYear+1}-02-01`) {
          setMembershipEndDate(`${currentYear+1}-02-28`);
        } else if (membershipStartDate === `${currentYear}-09-01`) {
          setMembershipEndDate(`${currentYear}-09-30`);
        }
      } else {
        if (
          membershipStartDate !== `${currentYear}-02-01` &&
          membershipStartDate !== `${currentYear}-09-01`
        ) {
          setMembershipStartDate(`${currentYear}-02-01`);
          return;
        }
        // Set end date based on start date
        if (membershipStartDate === `${currentYear}-02-01`) {
          setMembershipEndDate(`${currentYear}-02-28`);
        } else if (membershipStartDate === `${currentYear}-09-01`) {
          setMembershipEndDate(`${currentYear}-09-30`);
        }
      }
    } else if (membershipLevel === "talent" || membershipLevel === "engagement" || membershipLevel === "strategic") {
      // TALENT/ENGAGEMENT/STRATEGIC tiers use fixed July 1–June 30 cycle; dates set by useEffect above
      return;
    }
  }, [membershipStartDate, membershipLevel, currentYear]);

  const handleRegister = (e) => {
    e.preventDefault();
    setErrorMessage("");

    // Validation checks
    if (username.length < 8) {
      setErrorMessage("Username must be at least 8 characters.");
      return;
    }
    if (password !== confirmPassword) {
      setErrorMessage("Passwords do not match. Please re-enter.");
      return;
    }
    if (description.length > 500) {
      setErrorMessage("Description must be less than 500 characters.");
      return;
    }
    if (username.length > 50) {
      setErrorMessage("Username must be less than 50 characters.");
      return;
    }
    if (companyName.length > 100) {
      setErrorMessage("Company Name must be less than 100 characters");
      return;
    }
    if (!companyAddress) {
      setErrorMessage("Company address is required.");
      return;
    }
    if (!billingAddress) {
      setErrorMessage("Billing address is required.");
      return;
    }
    const selectedStartDate = new Date(membershipStartDate);
    const currentDate = new Date(todayString);
    if (selectedStartDate < currentDate && ((membershipLevel === "stakeholder") || (membershipLevel === "partner"))) {
      setErrorMessage("Partnership start date cannot be before today.");
      return;
    }
    if (primaryContact.length > 100) {
      setErrorMessage("Primary Contact Name must be less than 100 characters.");
      return;
    }
    if (primaryContact.length < 5) {
      setErrorMessage("Primary Contact Name must be at least 5 characters.");
      return;
    }
    if (primaryContactEmail.length > 100) {
      setErrorMessage("Primary Contact Email must be less than 100 characters.");
      return;
    }
    if (isAlumni === "Yes" && primaryContactMajor.length > 100) {
      setErrorMessage("Major must be less than 100 characters.");
      return;
    }

    const registrationData = {
      username,
      password,
      confirmPassword,
      companyName,
      companyAddress,
      billingAddress,
      description,
      membershipLevel,
      membershipStartDate,
      membershipEndDate,
      primaryContact,
      primaryContactEmail,
      isAlumni,
      primaryContactMajor,
    };

    axios.post("/register", registrationData)
      .then((res) => {
        if (res.data.success) {
          setSuccessMessage("Registration successful! Redirecting to home...");
          setTimeout(() => {
            navigate("/");
          }, 2000);
        } else {
          setErrorMessage("Registration failed. Please try again.");
        }
      })
      .catch((err) => {
        console.error("Registration error", err);
        setErrorMessage(`Error during registration: ${err.response.data.message}`);
      });
  };

  // Handle dismissing error or success messages
  const handleDismissMessage = () => {
    setErrorMessage("");
    setSuccessMessage("");
  };

  return (
    <div className="Homepage flex flex-col justify-center min-h-screen">
      <div className="login-container px-4 sm:px-6 lg:px-8">
        <div className="Homepage-page flex flex-col w-full max-w-4xl mx-auto py-6">
          {/* BOX 1: Title */}
          <div className="bg-white rounded-2xl shadow p-6 text-black mb-4">
            <h2 className="text-3xl font-arial normal-case no-underline">
              Register your company:
            </h2>
          </div>

          {/* BOX 2: Scrollable Form Fields */}
          <div className="bg-white rounded-2xl shadow p-6 text-black mb-4 max-h-[70vh] overflow-y-auto">
            <form id="registerForm" onSubmit={handleRegister}>
              <div className="flex flex-wrap -mx-2">
                {/* Username */}
                <div className="w-full md:w-1/2 px-2 mb-4">
                  <label className="block font-semibold mb-1">Username *</label>
                  <input
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    placeholder="Enter username here"
                    maxLength={50}
                    className="border rounded w-full p-2"
                    required
                  />
                </div>
                {/* Password with Toggle */}
                <div className="w-full md:w-1/2 px-2 mb-4">
                  <label className="block font-semibold mb-1">Password *</label>
                  <div className="flex items-center border rounded">
                    <input
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Enter password here"
                      pattern="^(?=.*[!@#$%^&*])(?=.{8,}).*$"
                      title="Password must be at least 8 characters long and contain at least one special character (e.g. !@#$%^&*)."
                      maxLength={50}
                      className="w-full p-2 outline-none placeholder-gray-500"
                      required
                    />
                    <button
                      type="button"
                      onClick={handleClickShowPassword}
                      className="p-2"
                    >
                      {showPassword ? <VisibilityIcon /> : <VisibilityOffIcon />}
                    </button>
                  </div>
                </div>
                {/* Confirm Password with Toggle */}
                <div className="w-full md:w-1/2 px-2 mb-4">
                  <label className="block font-semibold mb-1">Confirm Password *</label>
                  <div className="flex items-center border rounded">
                    <input
                      type={showPassword ? "text" : "password"}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Re-enter password here"
                      maxLength={50}
                      className="w-full p-2 outline-none placeholder-gray-500"
                      required
                    />
                    <button
                      type="button"
                      onClick={handleClickShowPassword}
                      className="p-2"
                    >
                      {showPassword ? <VisibilityIcon /> : <VisibilityOffIcon />}
                    </button>
                  </div>
                </div>
                {/* Company Name */}
                <div className="w-full md:w-1/2 px-2 mb-4">
                  <label className="block font-semibold mb-1">Company Name *</label>
                  <input
                    type="text"
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    placeholder="Enter company name here"
                    maxLength={100}
                    className="border rounded w-full p-2"
                    required
                  />
                </div>
                {/* Company Address */}
                <div className="w-full md:w-1/2 px-2 mb-4">
                  <label className="block font-semibold mb-1">Company Address *</label>
                  <LocationAutocomplete
                    idAndClassName="Register-CompanyAddress"
                    locationValue={companyAddress}
                    setLocationValue={setCompanyAddress}
                    key={101}
                  />
                  <label className="flex items-center mt-2">
                    <input
                      type="checkbox"
                      checked={billingSame}
                      onChange={(e) => setBillingSame(e.target.checked)}
                      className="mr-2"
                    />
                    Billing Address is same as company address
                  </label>
                </div>
                {/* Billing Address */}
                <div className="w-full md:w-1/2 px-2 mb-4">
                  <label className="block font-semibold mb-1">Billing Address *</label>
                  {billingSame ?
                    <input
                      type="text"
                      value={billingAddress}
                      placeholder="Enter an address here"
                      className="border rounded w-full p-2 bg-gray-100 cursor-not-allowed"
                      disabled
                    />
                  :
                    <LocationAutocomplete
                      idAndClassName="Register-BillingAddress"
                      locationValue={billingAddress}
                      setLocationValue={setBillingAddress}
                      key={102}
                      disabled={billingSame}
                    />
                  }
                </div>
                {/* Company Description */}
                <div className="w-full md:w-1/2 px-2 mb-4">
                  <label className="block font-semibold mb-1">Company Description</label>
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Enter description of company here"
                    className="border rounded w-full p-2"
                    maxLength={500}
                  />
                </div>
                {/* Partnership Level */}
                <div className="w-full md:w-1/2 px-2 mb-4">
                  <label className="block font-semibold mb-1">Partnership Level *</label>
                  <select
                    value={membershipLevel}
                    onChange={(e) => setMembershipLevel(e.target.value)}
                    className="border rounded w-full p-2"
                    required
                  >
                    <option value="careerfaironly">Career Fair Only</option>
                    <option value="talent">TALENT</option>
                    <option value="engagement">ENGAGEMENT</option>
                    <option value="strategic">STRATEGIC</option>
                  </select>
                  <div className="mt-1">
                    <a
                      href="/api/membershipOptions"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 underline text-sm"
                    >
                      Click here to explain partnership packages
                    </a>
                  </div>
                </div>
                {/* Partnership Start Date */}
                <div className="w-full md:w-1/2 px-2 mb-4">
                  <label className="block font-semibold mb-1">Partnership Start Date</label>
                  {membershipLevel === "careerfaironly" ? (
                    <select
                      value={membershipStartDate}
                      onChange={(e) => setMembershipStartDate(e.target.value)}
                      min={todayString}
                      className="border rounded w-full p-2"
                      required
                    >
                      {currentMonth < 7 && <option value={`${currentYear}-02-01`}>{`02/01/${currentYear}`}</option>}
                      <option value={`${currentYear}-09-01`}>{`09/01/${currentYear}`}</option>
                      {currentMonth >= 7 && <option value={`${currentYear+1}-02-01`}>{`02/01/${currentYear+1}`}</option>}
                    </select>
                  ) : membershipLevel === "talent" || membershipLevel === "engagement" || membershipLevel === "strategic" ? (
                    <input
                      type="date"
                      value={currentMonth >= 7 ? `${currentYear}-07-01` : `${currentYear}-07-01`}
                      readOnly
                      className="border rounded w-full p-2 bg-gray-100"
                      required
                    />
                  ) : (
                    <input
                      type="date"
                      value={membershipStartDate}
                      onChange={(e) => setMembershipStartDate(e.target.value)}
                      className="border rounded w-full p-2"
                      required
                    />
                  )}
                </div>
                {/* Partnership End Date (read-only) */}
                <div className="w-full md:w-1/2 px-2 mb-4">
                  <label className="block font-semibold mb-1">Partnership End Date</label>
                  <input
                    type="date"
                    value={membershipEndDate}
                    readOnly
                    className="border rounded w-full p-2 bg-gray-100"
                    required
                  />
                </div>
                {/* Primary Contact */}
                <div className="w-full md:w-1/2 px-2 mb-4">
                  <label className="block font-semibold mb-1">Primary Contact *</label>
                  <input
                    type="text"
                    value={primaryContact}
                    onChange={(e) => setPrimaryContact(e.target.value)}
                    placeholder="Enter your full name here"
                    maxLength={100}
                    className="border rounded w-full p-2"
                    required
                  />
                </div>
                {/* Primary Contact Email */}
                <div className="w-full md:w-1/2 px-2 mb-4">
                  <label className="block font-semibold mb-1">Primary Contact Email *</label>
                  <input
                    type="email"
                    value={primaryContactEmail}
                    onChange={(e) => setPrimaryContactEmail(e.target.value)}
                    placeholder="Enter your email here"
                    maxLength={100}
                    className="border rounded w-full p-2"
                    required
                  />
                </div>
                {/* Is Primary Contact an Alumni? */}
                <div className="w-full md:w-1/2 px-2 mb-4">
                  <label className="block font-semibold mb-1">Is Primary Contact an Alumni?</label>
                  <select
                    value={isAlumni}
                    onChange={(e) => setIsAlumni(e.target.value)}
                    className="border rounded w-full p-2"
                    required
                  >
                    <option value="No">No</option>
                    <option value="Yes">Yes</option>
                  </select>
                </div>
                {/* Major (conditionally required) */}
                <div className="w-full md:w-1/2 px-2 mb-4">
                  <label className="block font-semibold mb-1">Degree, Major, & Year</label>
                  <input
                    type="text"
                    value={primaryContactMajor}
                    onChange={(e) => setPrimaryContactMajor(e.target.value)}
                    placeholder="If you are an alumni, enter your degree, major, & year here"
                    maxLength={100}
                    className={`border rounded w-full p-2 ${
                      isAlumni !== "Yes" ? "bg-gray-100 cursor-not-allowed" : ""
                    }`}
                    disabled={isAlumni !== "Yes"}
                    required={isAlumni === "Yes"}
                  />
                </div>
              </div>
            </form>
          </div>

          {/* Action Buttons outside both boxes */}
          <div className="flex flex-wrap justify-center mt-6">
            <button
              type="submit"
              form="registerForm"
              className="bg-gray-300 text-black rounded px-4 py-2 mx-2 w-full sm:w-auto"
            >
              Register this Account
            </button>
            <button
              type="button"
              onClick={() => navigate("/")}
              className="bg-gray-300 text-black rounded px-4 py-2 mx-2 w-full sm:w-auto"
            >
              Return to Home
            </button>
          </div>

          {/* Display error or success messages as modal */}
          {(errorMessage || successMessage) && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-lg shadow-lg p-6 max-w-md w-full text-center">
                {errorMessage && (
                  <div className="mb-4">
                    <p className="text-xl font-semibold text-red-600">Error</p>
                    <p className="text-gray-700 mt-2">{errorMessage}</p>
                  </div>
                )}
                {successMessage && (
                  <div className="mb-4">
                    <p className="text-xl font-semibold text-green-600">Success</p>
                    <p className="text-gray-700 mt-2">{successMessage}</p>
                  </div>
                )}
                <button
                  onClick={handleDismissMessage}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md text-sm font-medium hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                >
                  Close
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Register;
